import InfoIcon from "@mui/icons-material/InfoOutlined";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useNotification } from "app/NotificationProvider";
import { mixpanel } from "common/analytics";
import { useTranslation } from "common/locales";
import {
  EventType,
  Field,
  getAllFieldsForAnimalGroupIdFromArray,
  useFields,
  useLatestManualReadingDate,
} from "model";
import React, { useMemo, useState } from "react";
import {
  createOneReading,
  deleteAllTilesForField,
  invalidateFieldData,
} from "services";
import { useUIStore } from "stores";
import { moveAnimalsIn } from "./moveAnimalsIn";

const ASYNC_THUNK_REJECTED_REQUEST_STATUS = "rejected";

export default function UpdateBiomassAfterMoveInDialog() {
  const defaultPostCover = 1500;

  const [postCover, setPostCover] = useState<number>(defaultPostCover);

  const handlePostCoverChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const cover = Number(e.target.value);
    setPostCover(cover);
    setBiomassWasChanged(true);
  };

  const { t } = useTranslation();
  const { notify } = useNotification();
  const moveAnimalsDialogDetails = useUIStore(
    (state) => state.moveAnimalsDialogDetails
  );
  const closeMoveOutEventDialog = useUIStore(
    (state) => state.closeMoveOutEventDialog
  );
  const currentFieldWithAnimals =
    moveAnimalsDialogDetails.currentFieldWithAnimals;
  const newFieldWithAnimals = moveAnimalsDialogDetails.newFieldWithAnimals;
  const currentFieldName = currentFieldWithAnimals?.name ?? "";

  const [animalMoveDate, setAnimalMoveDate] = useState<Date | null>(new Date());

  // Use React Query for fields data
  const { data: allFields = [] } = useFields();
  const fieldsForAnimalGroup: Field[] = useMemo(
    () =>
      getAllFieldsForAnimalGroupIdFromArray(
        allFields,
        currentFieldWithAnimals?.animalGroup ?? ""
      ),
    [allFields, currentFieldWithAnimals?.animalGroup]
  );
  const fieldGuids = useMemo(
    () => fieldsForAnimalGroup.map((f) => f.guid),
    [fieldsForAnimalGroup]
  );
  const latestMoveDateForAnimalGroup = useLatestManualReadingDate(fieldGuids);

  const [dateError, setDateError] = useState<boolean>(false);

  const [dateWasChanged, setDateWasChanged] = useState<boolean>(false);
  const [biomassWasChanged, setBiomassWasChanged] = useState<boolean>(false);

  return (
    <>
      <Dialog
        open={moveAnimalsDialogDetails.open}
        onClose={() => {
          closeMoveOutEventDialog();
          reset();
        }}
      >
        <DialogContent style={{ maxWidth: "300px", paddingBottom: "16px" }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12 }}>
              <Typography variant="h6">
                {t("moveOut.title", {
                  name: currentFieldName,
                  currentCover: currentFieldWithAnimals?.dryMatterNow,
                })}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 12 }}>
              <Typography variant="body1">
                {t("moveOut.fromField", {
                  fieldName: currentFieldName,
                })}
              </Typography>
              <Typography variant="body1">
                {t("moveOut.toField", {
                  fieldName: newFieldWithAnimals?.name,
                })}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 12 }}>
              <StyledDateInfo>
                <Typography variant="body1">
                  {t("moveOut.date.label")}
                </Typography>
                <Tooltip
                  title={t("moveOut.minDateInfo", {
                    minDate: latestMoveDateForAnimalGroup
                      ? latestMoveDateForAnimalGroup.toLocaleString()
                      : "",
                  })}
                >
                  <InfoIcon style={{ height: 20 }} />
                </Tooltip>
              </StyledDateInfo>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  minDate={latestMoveDateForAnimalGroup}
                  minTime={latestMoveDateForAnimalGroup}
                  maxDateTime={new Date()}
                  format="MMMM do HH:mm"
                  ampm={false}
                  value={animalMoveDate}
                  onChange={(value: Date | null) => {
                    if (
                      value &&
                      latestMoveDateForAnimalGroup &&
                      (value < latestMoveDateForAnimalGroup ||
                        value > new Date())
                    ) {
                      setDateError(true);
                    } else {
                      setDateError(false);
                    }
                    setAnimalMoveDate(value);
                    setDateWasChanged(true);
                  }}
                  slotProps={{
                    textField: {
                      variant: "standard",
                      error: dateError,
                      fullWidth: true,
                    },
                  }}
                  DialogProps={{
                    /*https://github.com/mui/material-ui/issues/26664*/
                    PaperProps: {
                      sx: { "& *:focus": { outline: "none" } },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid size={{ xs: 12, sm: 12 }}>
              <Typography variant="body1">
                {t("moveOut.subTitle", {
                  fieldName: currentFieldName,
                  currentCover: currentFieldWithAnimals?.dryMatterNow,
                })}
              </Typography>
              <Typography variant="body1">
                {" "}
                {t("moveOut.subTitle2")}{" "}
              </Typography>
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                variant="standard"
                fullWidth
                value={postCover}
                onChange={(
                  value: React.ChangeEvent<
                    HTMLInputElement | HTMLTextAreaElement
                  >
                ) => {
                  handlePostCoverChange(value);
                }}
                type="number"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {t("addCovers.single.unit")}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={dateError}
            color="secondary"
            onClick={async () => {
              await handleSave(
                false,
                currentFieldWithAnimals?.dryMatterNow ?? 0
              );
            }}
          >
            {t("moveOut.leaveIt.label")}
          </Button>
          <Button
            disabled={dateError}
            color="primary"
            onClick={async () => {
              await handleSave(true, postCover);
            }}
          >
            {t("moveOut.changeIt.label")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  function reset() {
    setPostCover(defaultPostCover);
    setAnimalMoveDate(new Date());
    setDateError(false);
    setDateWasChanged(false);
    setBiomassWasChanged(false);
  }

  async function handleSave(invalidateTiles: boolean, finalPostCover: number) {
    mixpanel.track("AnimalMoveIn with cover change started");
    if (biomassWasChanged) {
      mixpanel.track("AnimalMoveIn Farmer changed biomass.");
    }
    if (invalidateTiles) {
      mixpanel.track("AnimalMoveIn Farmer wants biomass to be updated.");
    }
    if (dateWasChanged) {
      mixpanel.track("AnimalMoveIn Date was changed.");
    }
    if (!newFieldWithAnimals) {
      return;
    }
    if (!currentFieldWithAnimals) {
      return;
    }
    if (dateError) {
      return;
    }

    const successfulMoveOut = await moveAnimalsOut(
      finalPostCover,
      invalidateTiles,
      currentFieldWithAnimals.guid,
      animalMoveDate ?? new Date(),
      notify,
      t,
      closeMoveOutEventDialog
    );

    if (successfulMoveOut) {
      await moveAnimalsIn(
        newFieldWithAnimals,
        currentFieldWithAnimals,
        animalMoveDate ?? new Date(),
        notify,
        t
      );
    }
    reset();
  }
}

async function moveAnimalsOut(
  finalPostCover: number,
  invalidateTiles: boolean,
  fieldGuid: string,
  dateTimeOut: Date,
  notify: (
    message: string,
    severity?: "success" | "error" | "warning" | "info"
  ) => void,
  t: (
    id: string,
    values?: Record<string, string | number | boolean | null | undefined>
  ) => string,
  closeMoveOutDialog: () => void
) {
  const timeDiff = 5 * 60000;

  const eventData = {
    eventType: EventType.GrazingEvent,
    dateTimeOut: dateTimeOut.toISOString(),
  };

  try {
    await createOneReading({
      guid: "new",
      manualDryMatterInput: {
        dryMatterValue: finalPostCover,
        field: fieldGuid,
        dateTimeMeasurementStart: dateTimeOut.toISOString(),
        dateTimeMeasurementEnd: new Date(
          dateTimeOut.getTime() + timeDiff
        ).toISOString(),
        event: eventData,
        kind: "user",
        verificationStatus: "Accepted",
      },
    });
    closeMoveOutDialog();
    notify(t("addCovers.success"));
    mixpanel.track("AnimalMoveOut with cover change success");
    if (invalidateTiles) {
      await deleteAllTilesForField(fieldGuid);
      invalidateFieldData(fieldGuid);
    }
    return true;
  } catch (e) {
    closeMoveOutDialog();
    notify(t("addCovers.failure"), "error");
    mixpanel.track("AnimalMoveOut with cover change failure");
    return false;
  }
}

const StyledDateInfo = styled("div")({
  display: "flex",
});
